// Service layer for all data access (mock or real API in the future)
// Best practice: async functions, all data from /src/data, easy to swap to real API

import { mockStudents } from '../data/mockStudents';
import { UserRole, StudentActivity } from '../data/types';
import { mockClasses } from '../data/mockClasses';
import { mockTeachers } from '../data/mockTeachers';
import { mockSchools } from '../data/mockSchools';
import { mockStandards } from '../data/mockStandards';
import { mockQuestions } from '../data/mockQuestions';
import { mockGames } from '../data/mockGames';
import { mockAchievements } from '../data/mockAchievements';
import { mockLearningPaths } from '../data/mockLearningPaths';
import { studentLearningPaths, standardDetailsMap } from '../data/mockStudentLearningPaths';
import { mockResources } from '../data/mockResources';
import { mockStudentGroups } from '../data/mockStudentGroups';
import { mockDistricts } from '../data/mockDistricts';
import { mockSystemConfig } from '../data/mockSystemConfig';
import { mockCsvImports } from '../data/mockCsvImports';
import { mockAuditLogs } from '../data/mockAuditLogs';
import { mockAdmins } from '../data/mockAdmins';
import { MATH_DOMAINS, getDomainById, getDomainByName } from '../data/domains';
import { mockStudentActivities } from '../data/mockStudentActivities';
import { mockStudentProgress } from '../data/mockStudentProgress';

// Example types (adjust as needed)
import {
  Student,
  Class,
  Teacher,
  School,
  Standard,
  Question,
  Game,
  Achievement,
  LearningPath,
  StudentGroup,
  District,
  SystemConfig,
  CsvImport,
  AuditLog,
  LearningPhaseProgressionRule,
  User,
  StudentProgressSummary,
} from '../data/types';

// Track the current active role and cache state
let _activeRole: UserRole | null = null;
let _cachedData: Record<string, any> = {};

export const dataService = {
  // Import classes from CSV file
  async importClassesFromCsv(file: File): Promise<CsvImport> {
    return new Promise((resolve) => {
      // Simulate processing delay
      setTimeout(() => {
        // In a real app, this would parse the CSV and create classes
        // For now, we'll simulate a successful import with some sample data
        const importResult: CsvImport = {
          id: `import-${Date.now().toString().slice(-5)}`,
          type: 'Classes',
          filename: file.name,
          timestamp: new Date().toISOString(),
          status: 'success',
          recordsProcessed: Math.floor(Math.random() * 10) + 5,
          recordsSucceeded: 0,
          recordsFailed: 0,
          errors: [],
          importedBy: 'user-1',
          duration: '00:00:03'
        };
        
        // Simulate some success and some failures
        importResult.recordsSucceeded = importResult.recordsProcessed - Math.floor(Math.random() * 3);
        importResult.recordsFailed = importResult.recordsProcessed - importResult.recordsSucceeded;
        
        if (importResult.recordsFailed > 0) {
          importResult.status = importResult.recordsSucceeded > 0 ? 'partial' : 'failed';
          
          // Add some sample errors
          for (let i = 0; i < importResult.recordsFailed; i++) {
            importResult.errors.push({
              row: Math.floor(Math.random() * 10) + 2, // Skip header row
              message: [
                'Invalid school ID',
                'Missing required field',
                'Invalid grade format',
                'Teacher ID not found'
              ][Math.floor(Math.random() * 4)]
            });
          }
        }
        
        // Add the import to mockCsvImports
        mockCsvImports.push(importResult);
        
        // Log this action to audit logs
        this.createAuditLog({
          userId: 'user-1',
          userName: 'John Admin',
          userRole: 'Admin',
          action: 'UPLOAD',
          resource: 'Classes',
          resourceId: null,
          details: `Imported classes from CSV: ${file.name}`,
          ipAddress: '192.168.1.100'
        });
        
        resolve(importResult);
      }, 1500); // Simulate network delay
    });
  },
  async getStudents(): Promise<Student[]> {
    return Promise.resolve(mockStudents);
  },
  async getClasses(): Promise<Class[]> {
    return Promise.resolve(mockClasses);
  },
  async getTeachers(): Promise<Teacher[]> {
    return Promise.resolve(mockTeachers);
  },
  async getAdmins(): Promise<User[]> {
    return Promise.resolve(mockAdmins);
  },
  async getAllUsers(): Promise<User[]> {
    // Combine all user types into a single array
    const students = await this.getStudents();
    const teachers = await this.getTeachers();
    const admins = await this.getAdmins();
    
    return [...students, ...teachers, ...admins];
  },
  async getSchools(): Promise<School[]> {
    return Promise.resolve(mockSchools);
  },
  async getStandards(): Promise<Standard[]> {
    return Promise.resolve(mockStandards);
  },
  async getQuestions(): Promise<Question[]> {
    return Promise.resolve(mockQuestions);
  },
  async getGames(): Promise<Game[]> {
    return Promise.resolve(mockGames);
  },
  async getAchievements(): Promise<Achievement[]> {
    return Promise.resolve(mockAchievements);
  },
  // Set the active role and clear cached data when role changes
  setActiveRole(role: UserRole | null): void {
    if (_activeRole !== role) {
      console.log(`Role changed from ${_activeRole} to ${role}, clearing cached data`);
      _activeRole = role;
      _cachedData = {}; // Clear all cached data when role changes
    }
  },
  
  // Get the current active role
  getActiveRole(): UserRole | null {
    return _activeRole;
  },
  
  async getLearningPaths(): Promise<LearningPath[]> {
    // If we're in a student session, filter to only show paths assigned to the student
    // In a real app, this would be based on the logged-in user
    if (localStorage.getItem('isStudentSession') === 'true') {
      // For demo purposes, we'll combine the regular learning paths with the student-specific paths
      // In a real app, this would filter based on the current student's assigned paths
      const regularPaths = mockLearningPaths.filter(path => 
        path.isPreset || // Show all preset paths
        path.assignedTo.some(assignment => assignment.assignedToType === 'Student') // And paths assigned to students
      );
      
      // Combine with student-specific learning paths
      return Promise.resolve([...regularPaths, ...studentLearningPaths]);
    }
    
    // For teachers, return all paths including student-specific ones
    return Promise.resolve([...mockLearningPaths, ...studentLearningPaths]);
  },
  async getResources() {
    return Promise.resolve(mockResources);
  },
  async getStudentGroups(): Promise<StudentGroup[]> {
    return Promise.resolve(mockStudentGroups);
  },
  async getStudentGroup(groupId: string): Promise<StudentGroup | undefined> {
    const groups = await this.getStudentGroups();
    return groups.find(group => group.id === groupId);
  },
  async createStudentGroup(group: Omit<StudentGroup, 'id' | 'createdAt' | 'updatedAt' | 'status'>): Promise<StudentGroup> {
    const newGroup: StudentGroup = {
      ...group,
      id: `group-${Date.now().toString().slice(-3)}`,
      status: 'active', // Default to active
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    mockStudentGroups.push(newGroup);
    return newGroup;
  },
  
  async toggleGroupStatus(groupId: string): Promise<StudentGroup> {
    const index = mockStudentGroups.findIndex(g => g.id === groupId);
    if (index !== -1) {
      mockStudentGroups[index] = {
        ...mockStudentGroups[index],
        status: mockStudentGroups[index].status === 'active' ? 'inactive' : 'active',
        updatedAt: new Date().toISOString(),
      };
      return mockStudentGroups[index];
    }
    throw new Error('Group not found');
  },
  async updateStudentGroup(group: StudentGroup): Promise<StudentGroup> {
    const index = mockStudentGroups.findIndex(g => g.id === group.id);
    if (index !== -1) {
      mockStudentGroups[index] = {
        ...group,
        updatedAt: new Date().toISOString(),
      };
      return mockStudentGroups[index];
    }
    throw new Error('Group not found');
  },
  async deleteStudentGroup(groupId: string): Promise<boolean> {
    const index = mockStudentGroups.findIndex(g => g.id === groupId);
    if (index !== -1) {
      mockStudentGroups.splice(index, 1);
      return true;
    }
    return false;
  },
  
  // Super Admin specific methods
  async getDistricts(): Promise<District[]> {
    return Promise.resolve(mockDistricts);
  },
  
  async getDistrict(districtId: string): Promise<District | undefined> {
    const districts = await this.getDistricts();
    return districts.find(district => district.id === districtId);
  },

  async createDistrict(district: Omit<District, 'id' | 'createdAt' | 'updatedAt'>): Promise<District> {
    const newDistrict: District = {
      ...district,
      id: `district-${Date.now().toString().slice(-5)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    mockDistricts.push(newDistrict);

    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'CREATE',
      resource: 'District',
      resourceId: newDistrict.id,
      details: `Created new district: ${newDistrict.name}`,
      ipAddress: '192.168.1.100'
    });

    return newDistrict;
  },

  async updateDistrict(district: District): Promise<District> {
    const index = mockDistricts.findIndex(d => d.id === district.id);
    if (index !== -1) {
      mockDistricts[index] = {
        ...district,
        updatedAt: new Date().toISOString(),
      };

      // Log this action to audit logs
      this.createAuditLog({
        userId: 'user-1',
        userName: 'John Admin',
        userRole: 'SuperAdmin',
        action: 'UPDATE',
        resource: 'District',
        resourceId: district.id,
        details: `Updated district: ${district.name}`,
        ipAddress: '192.168.1.100'
      });

      return mockDistricts[index];
    }
    throw new Error('District not found');
  },

  async deleteDistrict(districtId: string): Promise<boolean> {
    const index = mockDistricts.findIndex(d => d.id === districtId);
    if (index !== -1) {
      const deletedDistrict = mockDistricts[index];
      mockDistricts.splice(index, 1);

      // Log this action to audit logs
      this.createAuditLog({
        userId: 'user-1',
        userName: 'John Admin',
        userRole: 'SuperAdmin',
        action: 'DELETE',
        resource: 'District',
        resourceId: districtId,
        details: `Deleted district: ${deletedDistrict.name}`,
        ipAddress: '192.168.1.100'
      });

      return true;
    }
    return false;
  },
  
  async getSystemConfig(): Promise<SystemConfig> {
    return Promise.resolve(mockSystemConfig);
  },
  
  async updateSystemConfig(config: Partial<SystemConfig>): Promise<SystemConfig> {
    Object.assign(mockSystemConfig, {
      ...config,
      updatedAt: new Date().toISOString()
    });
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1', // In a real app, this would be the current user
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'UPDATE',
      resource: 'SystemConfiguration',
      resourceId: mockSystemConfig.id,
      details: 'Updated system configuration',
      ipAddress: '192.168.1.100'
    });
    
    return mockSystemConfig;
  },
  
  async getCsvImports(): Promise<CsvImport[]> {
    return Promise.resolve(mockCsvImports);
  },
  
  async getCsvImport(importId: string): Promise<CsvImport | undefined> {
    return mockCsvImports.find(imp => imp.id === importId);
  },
  
  async triggerCsvImport(type: string, file: File): Promise<CsvImport> {
    // In a real app, this would upload the file and process it
    const newImport: CsvImport = {
      id: `import-${Date.now().toString().slice(-5)}`,
      type,
      filename: file.name,
      timestamp: new Date().toISOString(),
      status: 'processing',
      recordsProcessed: 0,
      recordsSucceeded: 0,
      recordsFailed: 0,
      errors: [],
      importedBy: 'user-1', // In a real app, this would be the current user
      duration: '00:00:00'
    };
    
    mockCsvImports.unshift(newImport);
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'MANUAL_IMPORT',
      resource: 'CSV',
      resourceId: newImport.id,
      details: `Manually triggered ${type} import: ${file.name}`,
      ipAddress: '192.168.1.100'
    });
    
    // Simulate processing
    setTimeout(() => {
      const index = mockCsvImports.findIndex(imp => imp.id === newImport.id);
      if (index !== -1) {
        mockCsvImports[index] = {
          ...mockCsvImports[index],
          status: Math.random() > 0.2 ? 'success' : 'partial',
          recordsProcessed: Math.floor(Math.random() * 1000) + 100,
          recordsSucceeded: Math.floor(Math.random() * 900) + 100,
          recordsFailed: Math.floor(Math.random() * 10),
          duration: '00:01:30'
        };
        
        if (mockCsvImports[index].recordsFailed > 0) {
          mockCsvImports[index].errors = [
            { row: Math.floor(Math.random() * 100) + 1, message: 'Missing required field' }
          ];
        }
      }
    }, 1500);
    
    return newImport;
  },
  
  async getAuditLogs(): Promise<AuditLog[]> {
    return Promise.resolve(mockAuditLogs);
  },
  
  async createAuditLog(log: Omit<AuditLog, 'id' | 'timestamp'>): Promise<AuditLog> {
    const newLog: AuditLog = {
      ...log,
      id: `log-${Date.now().toString().slice(-5)}`,
      timestamp: new Date().toISOString()
    };
    
    mockAuditLogs.unshift(newLog);
    return newLog;
  },
  
  async getLearningPhaseProgressionRules(): Promise<LearningPhaseProgressionRule[]> {
    const rules = [mockSystemConfig.learningPhaseProgression.districtDefault];
    
    // Add school overrides
    Object.values(mockSystemConfig.learningPhaseProgression.schoolOverrides).forEach(rule => {
      rules.push(rule);
    });
    
    return rules;
  },
  
  async updateLearningPhaseProgressionRule(rule: LearningPhaseProgressionRule): Promise<LearningPhaseProgressionRule> {
    if (rule.isDefault) {
      mockSystemConfig.learningPhaseProgression.districtDefault = rule;
    } else {
      // Find which school this rule belongs to
      for (const [schoolId, schoolRule] of Object.entries(mockSystemConfig.learningPhaseProgression.schoolOverrides)) {
        if (schoolRule.id === rule.id) {
          mockSystemConfig.learningPhaseProgression.schoolOverrides[schoolId] = rule;
          break;
        }
      }
    }
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'UPDATE',
      resource: 'ProgressionRule',
      resourceId: rule.id,
      details: `Updated learning phase progression rule: ${rule.name}`,
      ipAddress: '192.168.1.100'
    });
    
    return rule;
  },
  
  async startConcurrentAccess(userId: string, mode: 'ReadOnly' | 'FullAccess', purpose: string): Promise<boolean> {
    // In a real app, this would set up a concurrent access session
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'CONCURRENT_ACCESS',
      resource: 'User',
      resourceId: userId,
      details: `Started concurrent access session as ${userId} (${mode} Mode). Purpose: ${purpose}`,
      ipAddress: '192.168.1.100'
    });
    
    return true;
  },
  
  async endConcurrentAccess(userId: string): Promise<boolean> {
    // In a real app, this would end a concurrent access session
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'CONCURRENT_ACCESS',
      resource: 'User',
      resourceId: userId,
      details: `Ended concurrent access session`,
      ipAddress: '192.168.1.100'
    });
    
    return true;
  },
  
  async uploadGame(gameData: Partial<Game>, file: File): Promise<Game> {
    // In a real app, this would upload and process the game file
    const newGame: Game = {
      id: `game-${Date.now().toString().slice(-5)}`,
      title: gameData.title || 'New Game',
      primaryStandardId: gameData.primaryStandardId || '',
      gradeLevel: gameData.gradeLevel || 'K',
      difficulty: gameData.difficulty || 'Medium',
      type: gameData.type || 'Arcade',
      theme: gameData.theme || 'Default',
      thumbnail: gameData.thumbnail || '/images/games/default.png',
      supportedStandards: gameData.supportedStandards || []
    };
    
    mockGames.push(newGame);
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'UPLOAD',
      resource: 'Game',
      resourceId: newGame.id,
      details: `Uploaded new game: ${newGame.title}`,
      ipAddress: '192.168.1.100'
    });
    
    return newGame;
  },
  
  async uploadQuestions(file: File): Promise<CsvImport> {
    // Reuse the CSV import functionality
    return this.triggerCsvImport('Questions', file);
  },
  
  async getQuestionById(questionId: string): Promise<Question | null> {
    const questions = await this.getQuestions();
    const question = questions.find(q => q.id === questionId);
    return question || null;
  },

  async getStandardById(standardId: string): Promise<Standard | null> {
    const standards = await this.getStandards();
    const standard = standards.find(s => s.id === standardId);
    return standard || null;
  },

  async updateQuestion(questionId: string, questionData: Partial<Question>): Promise<Question> {
    const questions = await this.getQuestions();
    const index = questions.findIndex(q => q.id === questionId);
    
    if (index === -1) {
      throw new Error('Question not found');
    }
    
    // Update the question with new data
    const updatedQuestion: Question = {
      ...questions[index],
      ...questionData,
      id: questionId // Ensure ID doesn't change
    };
    
    mockQuestions[index] = updatedQuestion;
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'UPDATE',
      resource: 'Question',
      resourceId: questionId,
      details: `Updated question`,
      ipAddress: '192.168.1.100'
    });
    
    return updatedQuestion;
  },

  async removeQuestion(questionId: string): Promise<boolean> {
    const index = mockQuestions.findIndex(q => q.id === questionId);
    if (index !== -1) {
      mockQuestions.splice(index, 1);
      
      // Log this action to audit logs
      this.createAuditLog({
        userId: 'user-1',
        userName: 'John Admin',
        userRole: 'SuperAdmin',
        action: 'DELETE',
        resource: 'Question',
        resourceId: questionId,
        details: `Removed question`,
        ipAddress: '192.168.1.100'
      });
      
      return true;
    }
    return false;
  },
  
  async getSchool(schoolId: string): Promise<School | undefined> {
    const schools = await this.getSchools();
    return schools.find(school => school.id === schoolId);
  },

  async createSchool(school: Omit<School, 'id'>): Promise<School> {
    const newSchool: School = {
      ...school,
      id: `school-${Date.now().toString().slice(-5)}`,
    };
    mockSchools.push(newSchool);
    
    // Log this action to audit logs
    this.createAuditLog({
      userId: 'user-1',
      userName: 'John Admin',
      userRole: 'SuperAdmin',
      action: 'CREATE',
      resource: 'School',
      resourceId: newSchool.id,
      details: `Created new school: ${newSchool.name}`,
      ipAddress: '192.168.1.100'
    });
    
    return newSchool;
  },

  async updateSchool(school: School): Promise<School> {
    const index = mockSchools.findIndex(s => s.id === school.id);
    if (index !== -1) {
      mockSchools[index] = school;
      
      // Log this action to audit logs
      this.createAuditLog({
        userId: 'user-1',
        userName: 'John Admin',
        userRole: 'SuperAdmin',
        action: 'UPDATE',
        resource: 'School',
        resourceId: school.id,
        details: `Updated school: ${school.name}`,
        ipAddress: '192.168.1.100'
      });
      
      return school;
    }
    throw new Error('School not found');
  },

  async deleteSchool(schoolId: string): Promise<boolean> {
    const index = mockSchools.findIndex(s => s.id === schoolId);
    if (index !== -1) {
      const deletedSchool = mockSchools[index];
      mockSchools.splice(index, 1);
      
      // Log this action to audit logs
      this.createAuditLog({
        userId: 'user-1',
        userName: 'John Admin',
        userRole: 'SuperAdmin',
        action: 'DELETE',
        resource: 'School',
        resourceId: schoolId,
        details: `Deleted school: ${deletedSchool.name}`,
        ipAddress: '192.168.1.100'
      });
      
      return true;
    }
    return false;
  },
  
  // Learning Path Designer specific methods
  getDomainIdByName(name: string): string {
    // Normalize domain names to match MATH_DOMAINS format
    const normalizedName = name
      .replace('&', 'and')
      .replace('—', '-');
    
    const domain = MATH_DOMAINS.find(d => {
      return d.name.toLowerCase() === normalizedName.toLowerCase() ||
             d.name.toLowerCase().includes(normalizedName.toLowerCase());
    });
    
    if (!domain) {
      console.warn(`Domain not found: ${name}`);
      return MATH_DOMAINS[0].id; // Default to first domain if not found
    }
    
    return domain.id;
  },
  
  getDomainNameById(domainId: string): string {
    const domain = MATH_DOMAINS.find(d => d.id === domainId);
    return domain ? domain.name : '';
  },
  
  getDomains() {
    return MATH_DOMAINS;
  },
  
  getGrades() {
    return ['K', '1', '2', '3', '4', '5', '6', '7', '8'];
  },
  
  // Student Activities Methods
  async getStudentActivities(studentId: string): Promise<StudentActivity[]> {
    return Promise.resolve(
      mockStudentActivities
        .filter(activity => activity.studentId === studentId)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    );
  },
  
  async getRecentStudentActivities(studentId: string, limit: number = 5): Promise<StudentActivity[]> {
    return Promise.resolve(
      mockStudentActivities
        .filter(activity => activity.studentId === studentId)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, limit)
    );
  },
  
  async addStudentActivity(activity: Omit<StudentActivity, 'id'>): Promise<StudentActivity> {
    const newActivity = {
      ...activity,
      id: `activity-${Date.now().toString().slice(-6)}`
    };
    
    mockStudentActivities.push(newActivity);
    return Promise.resolve(newActivity);
  },
  
  // Student Progress Methods
  async getStudentProgress(studentId: string): Promise<StudentProgressSummary | undefined> {
    return Promise.resolve(
      mockStudentProgress.find(progress => progress.studentId === studentId)
    );
  },
  
  async getStudentPathProgress(studentId: string, pathId: string): Promise<number> {
    const progress = mockStudentProgress.find(p => p.studentId === studentId);
    if (!progress) return Promise.resolve(0);
    
    const pathProgress = progress.pathProgress.find(p => p.pathId === pathId);
    return Promise.resolve(pathProgress ? pathProgress.completionPercentage : 0);
  },
  
  async getQuestionsForStandard(standardId: string, difficulty: 'Easy' | 'Medium' | 'Hard'): Promise<any[]> {
    // In a real app, this would fetch from an API based on the standard and difficulty
    const questions = [
      {
        id: `q-${standardId}-1`,
        text: difficulty === 'Easy' 
          ? 'What is 1/4 + 2/4?' 
          : difficulty === 'Medium' 
          ? 'What is 1/3 + 2/5?' 
          : 'Solve: 2 3/4 + 1 5/8',
        type: 'multiple-choice',
        options: difficulty === 'Easy' 
          ? ['1/2', '3/4', '3/8', '1/8'] 
          : difficulty === 'Medium'
          ? ['5/15', '11/15', '3/8', '8/15']
          : ['3 7/8', '4 3/8', '4 1/3', '3 3/8'],
        correctAnswer: difficulty === 'Easy' 
          ? 1 // 3/4
          : difficulty === 'Medium'
          ? 1 // 11/15
          : 0 // 3 7/8
      },
      {
        id: `q-${standardId}-2`,
        text: difficulty === 'Easy' 
          ? 'What is 1/3 + 1/3?' 
          : difficulty === 'Medium' 
          ? 'What is 3/8 + 1/4?' 
          : 'Solve: 2 1/6 + 3 5/12',
        type: 'multiple-choice',
        options: difficulty === 'Easy' 
          ? ['1/6', '2/3', '1/9', '2/9'] 
          : difficulty === 'Medium'
          ? ['4/8', '5/8', '4/12', '7/12']
          : ['5 1/4', '5 7/12', '5 3/4', '6 1/12'],
        correctAnswer: difficulty === 'Easy' 
          ? 1 // 2/3
          : difficulty === 'Medium'
          ? 1 // 5/8
          : 1 // 5 7/12
      },
      {
        id: `q-${standardId}-3`,
        text: difficulty === 'Easy' 
          ? 'Calculate 3/4 + 1/4' 
          : difficulty === 'Medium' 
          ? 'Calculate 2/5 + 3/10' 
          : 'Add the mixed numbers: 3 2/3 + 2 3/4',
        type: 'multiple-choice',
        options: difficulty === 'Easy' 
          ? ['1', '4/4', '4/8', '2/4'] 
          : difficulty === 'Medium'
          ? ['5/15', '1/2', '7/10', '5/10']
          : ['5 5/7', '6 5/12', '5 5/12', '6 1/12'],
        correctAnswer: difficulty === 'Easy' 
          ? 0 // 1
          : difficulty === 'Medium'
          ? 2 // 7/10
          : 2 // 5 5/12
      },
      {
        id: `q-${standardId}-4`,
        text: difficulty === 'Easy' 
          ? 'What is 2/3 + 1/3?' 
          : difficulty === 'Medium' 
          ? 'What is 5/6 - 1/3?' 
          : 'If a recipe requires 2 3/4 cups of flour and you want to make 1 1/2 batches, how much flour do you need?',
        type: 'multiple-choice',
        options: difficulty === 'Easy' 
          ? ['3/6', '1', '3/3', '3/9'] 
          : difficulty === 'Medium'
          ? ['4/6', '1/2', '4/3', '1/6']
          : ['4 1/8 cups', '4 cups', '3 1/4 cups', '4 1/4 cups'],
        correctAnswer: difficulty === 'Easy' 
          ? 1 // 1
          : difficulty === 'Medium'
          ? 1 // 1/2
          : 0 // 4 1/8 cups
      },
      {
        id: `q-${standardId}-5`,
        text: difficulty === 'Easy' 
          ? 'Solve: 1/2 + 1/2' 
          : difficulty === 'Medium' 
          ? 'Solve: 3/4 - 1/3' 
          : 'A baker used 3/4 of a bag of flour for bread and 2/3 of the remainder for cookies. What fraction of the original bag was used for cookies?',
        type: 'multiple-choice',
        options: difficulty === 'Easy' 
          ? ['2/4', '2/2', '1', '1/4'] 
          : difficulty === 'Medium'
          ? ['2/4', '5/12', '2/7', '1/12']
          : ['1/6', '1/4', '1/2', '1/3'],
        correctAnswer: difficulty === 'Easy' 
          ? 2 // 1
          : difficulty === 'Medium'
          ? 1 // 5/12
          : 0 // 1/6
      }
    ];
    
    return Promise.resolve(questions);
  },

  // Get learning paths for a specific student
  async getStudentLearningPaths(studentId: string): Promise<LearningPath[]> {
    // Filter student learning paths based on assignments to the student
    const paths = studentLearningPaths.filter(path => {
      return path.assignedTo.some(assignment => 
        assignment.assignedToType === 'Student' && 
        assignment.assignedToId === studentId
      );
    });
    
    return Promise.resolve(paths);
  },

  // Get the standard details mapping for easy access
  async getStandardDetailsMap(): Promise<Record<string, typeof mockStandards[0]>> {
    return Promise.resolve(standardDetailsMap);
  }
};

// In the future, swap Promise.resolve(mockData) with real API calls (e.g., fetch)
